package com.flowyride.feature.planner.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Slider
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.flowyride.core.designsystem.theme.FlowyColors
import com.flowyride.core.domain.loop.CompassDirection
import com.flowyride.feature.planner.LoopTargetKind
import com.flowyride.feature.planner.LoopUiState
import com.flowyride.feature.planner.PlannerAction
import com.flowyride.feature.planner.PlannerMode
import com.flowyride.feature.planner.PlannerUiState
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.decrease
import com.flowyride.feature.planner.resources.departure_label
import com.flowyride.feature.planner.resources.direction_e
import com.flowyride.feature.planner.resources.direction_n
import com.flowyride.feature.planner.resources.direction_ne
import com.flowyride.feature.planner.resources.direction_nw
import com.flowyride.feature.planner.resources.direction_s
import com.flowyride.feature.planner.resources.direction_se
import com.flowyride.feature.planner.resources.direction_sw
import com.flowyride.feature.planner.resources.direction_w
import com.flowyride.feature.planner.resources.increase
import com.flowyride.feature.planner.resources.loop_direction_any
import com.flowyride.feature.planner.resources.loop_direction_label
import com.flowyride.feature.planner.resources.loop_generate
import com.flowyride.feature.planner.resources.loop_from
import com.flowyride.feature.planner.resources.loop_generating
import com.flowyride.feature.planner.resources.label_my_position
import com.flowyride.feature.planner.resources.loop_hint
import com.flowyride.feature.planner.resources.loop_reroll
import com.flowyride.feature.planner.resources.loop_target_distance
import com.flowyride.feature.planner.resources.loop_target_duration
import com.flowyride.feature.planner.resources.mode_loop
import com.flowyride.feature.planner.ui.formatDuration
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

private const val DISTANCE_STEP_KM = 10
private const val DURATION_STEP_MINUTES = 15

/** Departure, length and direction of the loop to generate (the button sits in [LoopHeader]). */
@Composable
internal fun LoopSection(
    state: PlannerUiState,
    onAction: (PlannerAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    val loop = state.loop
    val departure = state.stops.first()
    Column(modifier, verticalArrangement = Arrangement.spacedBy(12.dp)) {
        PlaceSearchField(
            label = if (departure.followsRider) {
                stringResource(Res.string.label_my_position, stringResource(Res.string.departure_label))
            } else {
                stringResource(Res.string.departure_label)
            },
            markerColor = FlowyColors.Departure,
            state = departure,
            onQueryChange = { onAction(PlannerAction.QueryChanged(departure.id, it)) },
            onPlaceSelected = { onAction(PlannerAction.PlaceSelected(departure.id, it)) },
            onClear = { onAction(PlannerAction.FieldCleared(departure.id)) },
        )

        SingleChoiceSegmentedButtonRow(Modifier.fillMaxWidth()) {
            LoopTargetKind.entries.forEachIndexed { index, kind ->
                SegmentedButton(
                    selected = kind == loop.targetKind,
                    onClick = { onAction(PlannerAction.LoopTargetKindChanged(kind)) },
                    shape = SegmentedButtonDefaults.itemShape(index, LoopTargetKind.entries.size),
                    label = {
                        Text(stringResource(if (kind == LoopTargetKind.DISTANCE) Res.string.loop_target_distance else Res.string.loop_target_duration))
                    },
                )
            }
        }
        TargetPicker(loop, onAction)

        Text(stringResource(Res.string.loop_direction_label), style = MaterialTheme.typography.labelLarge)
        CompassPicker(loop.direction, onSelect = { onAction(PlannerAction.LoopDirectionChanged(it)) })

        Text(
            stringResource(Res.string.loop_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

/** Collapsed sheet of the loop mode: what will be generated, and the button. */
@Composable
internal fun LoopHeader(state: PlannerUiState, onAction: (PlannerAction) -> Unit, modifier: Modifier = Modifier) {
    val loop = state.loop
    val departure = state.stops.first()
    Row(modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Column(Modifier.weight(1f)) {
            Text(
                if (loop.targetKind == LoopTargetKind.DISTANCE) "${loop.distanceKm} km" else formatDuration(loop.durationMinutes * 60.0),
                style = MaterialTheme.typography.titleLarge,
            )
            Text(
                stringResource(Res.string.loop_from, departure.displayName(stringResource(Res.string.departure_label))),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
        Button(onClick = { onAction(PlannerAction.GenerateLoop) }, enabled = state.canGenerateLoop) {
            if (loop.isGenerating) {
                CircularProgressIndicator(Modifier.size(18.dp), strokeWidth = 2.dp, color = MaterialTheme.colorScheme.onPrimary)
                Spacer(Modifier.size(8.dp))
                Text(stringResource(Res.string.loop_generating))
            } else {
                Text(stringResource(Res.string.loop_generate))
            }
        }
    }
}

/** Shown in the route mode once a loop was generated: same settings, another shape. */
@Composable
internal fun RerollLoopButton(state: PlannerUiState, onAction: (PlannerAction) -> Unit, modifier: Modifier = Modifier) {
    if (state.loop.lastRequest == null) return
    OutlinedButton(
        onClick = { onAction(PlannerAction.RerollLoop) },
        enabled = !state.loop.isGenerating,
        modifier = modifier.fillMaxWidth(),
    ) {
        if (state.loop.isGenerating) {
            CircularProgressIndicator(Modifier.size(18.dp), strokeWidth = 2.dp)
            Spacer(Modifier.size(8.dp))
            Text(stringResource(Res.string.loop_generating))
        } else {
            Text(stringResource(Res.string.loop_reroll))
        }
    }
}

@Composable
private fun TargetPicker(loop: LoopUiState, onAction: (PlannerAction) -> Unit) {
    val distance = loop.targetKind == LoopTargetKind.DISTANCE
    val range = if (distance) LoopUiState.DISTANCE_KM else LoopUiState.DURATION_MINUTES
    val step = if (distance) DISTANCE_STEP_KM else DURATION_STEP_MINUTES
    val value = if (distance) loop.distanceKm else loop.durationMinutes
    fun change(newValue: Int) = onAction(
        if (distance) PlannerAction.LoopDistanceChanged(newValue) else PlannerAction.LoopDurationChanged(newValue),
    )

    Column {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            StepButton("−", stringResource(Res.string.decrease), enabled = value > range.first) { change(value - step) }
            Text(
                if (distance) "$value km" else formatDuration(value * 60.0),
                Modifier.weight(1f),
                style = MaterialTheme.typography.titleLarge,
                textAlign = TextAlign.Center,
            )
            StepButton("+", stringResource(Res.string.increase), enabled = value < range.last) { change(value + step) }
        }
        Slider(
            value = value.toFloat(),
            onValueChange = { change((it / step).roundToInt() * step) },
            valueRange = range.first.toFloat()..range.last.toFloat(),
        )
    }
}

@Composable
private fun StepButton(symbol: String, description: String, enabled: Boolean, onClick: () -> Unit) {
    TextButton(onClick = onClick, enabled = enabled, modifier = Modifier.semantics { contentDescription = description }) {
        Text(symbol, style = MaterialTheme.typography.titleLarge)
    }
}

/** Compass rose: eight directions around "any". */
@Composable
private fun CompassPicker(selected: CompassDirection?, onSelect: (CompassDirection?) -> Unit) {
    val rows = listOf(
        listOf(CompassDirection.NW, CompassDirection.N, CompassDirection.NE),
        listOf(CompassDirection.W, null, CompassDirection.E),
        listOf(CompassDirection.SW, CompassDirection.S, CompassDirection.SE),
    )
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(4.dp), horizontalAlignment = Alignment.CenterHorizontally) {
        rows.forEach { row ->
            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                row.forEach { direction ->
                    val label = direction?.let { stringResource(it.label) } ?: stringResource(Res.string.loop_direction_any)
                    val buttonModifier = Modifier.size(width = 72.dp, height = 40.dp)
                    val padding = PaddingValues(horizontal = 4.dp)
                    if (direction == selected) {
                        FilledTonalButton(onClick = { onSelect(direction) }, modifier = buttonModifier, contentPadding = padding) {
                            Text(label)
                        }
                    } else {
                        OutlinedButton(onClick = { onSelect(direction) }, modifier = buttonModifier, contentPadding = padding) {
                            Text(label)
                        }
                    }
                }
            }
        }
    }
}

private val CompassDirection.label: StringResource
    get() = when (this) {
        CompassDirection.N -> Res.string.direction_n
        CompassDirection.NE -> Res.string.direction_ne
        CompassDirection.E -> Res.string.direction_e
        CompassDirection.SE -> Res.string.direction_se
        CompassDirection.S -> Res.string.direction_s
        CompassDirection.SW -> Res.string.direction_sw
        CompassDirection.W -> Res.string.direction_w
        CompassDirection.NW -> Res.string.direction_nw
    }
